// Generic 2D menu focus manager. Used by HomeScreen's main menu, the
// changing room 2D grid, MapSelectScreen's card row, and the game-end
// modal's single button. One class so every menu supports mouse, keyboard
// arrows, and gamepad (dpad + left stick + A) with the exact same
// behaviour — including audio feedback.
//
// Usage pattern:
//   const nav = new MenuNav({ items, onHoverSound, onClickSound });
//   nav.mount();            // starts keyboard/gamepad listeners + focuses first item
//   nav.dispose();          // on screen teardown
//
// Items are a flat list, each tagged with (row, col). Navigation rules:
//   ← / → — move within the row, clamped at edges (no wrap — wrapping is
//           disorienting on small lists and the user's spec didn't ask for
//           it). If no left/right neighbour exists in the current row, the
//           press is a no-op.
//   ↑ / ↓ — move to the nearest item in the adjacent row whose column is
//           closest to the current column. Lets grids with irregular row
//           widths (e.g. hats 6×3 + colors 7 + name-row 2) behave
//           intuitively — pressing Down from the first hat goes to the
//           first colour, not just "the next item in z-order".
//   Enter / A — activate (calls item.onActivate + plays click).
//   mouseenter — focus sync (moves the marker + plays hover). Mouse and
//           nav share the same focus state, so a click after a keyboard
//           arrow sequence activates whatever the user can see is lit.
//   click — activate (mirror of Enter/A — uses the same codepath so the
//           click sound plays consistently).
//
// Focus visual: the manager adds a `.focused` CSS class to the current
// item's element. Each menu's stylesheet styles `.focused` identically to
// `:hover` so mouse and controller users see the same highlight.

import { menuAudio } from './MenuAudio';
import { MenuGamepad, type MenuGamepadDirection } from './MenuGamepad';

/** Activation source passed to onActivate — lets callers that wrap a native
 *  DOM element whose bubble fires its own side-effect (e.g. a transparent
 *  <input type="color"> that opens the OS picker on click) distinguish
 *  mouse activations (where the native behaviour already fired) from
 *  keyboard/controller activations (where they need to synthesise the
 *  action themselves via el.click()). */
export type MenuActivationSource = 'mouse' | 'key';

export interface MenuItem {
  /** The DOM element that receives the .focused class + mouseenter/click
   *  listeners. Usually a <button>, but any focusable-ish element works. */
  el: HTMLElement;
  /** 0-indexed row in the logical grid. */
  row: number;
  /** 0-indexed column in the logical grid. */
  col: number;
  /** Called when the user activates this item. `source` indicates whether
   *  it came from a mouse click (where any native bubble side-effects on
   *  the element already fired) or keyboard / controller (where they
   *  didn't). */
  onActivate: (source: MenuActivationSource) => void;
  /** Optional hook when this item receives focus — e.g. moving DOM focus
   *  to a nested <input>. Fires AFTER the hover sound so the caller
   *  doesn't need to gate side effects on "is this a user action or the
   *  initial focus" — they just run. */
  onFocus?: () => void;
  /** Optional left/right "adjust" handler. When present, ArrowLeft /
   *  ArrowRight + dpad-left / dpad-right + left-stick-left/right call
   *  this with delta -1 / +1 instead of moving focus to a sibling item.
   *  Sliders use it so a controller can drag values without the focus
   *  cursor jumping away — the slider stays focused while the user
   *  scrubs. Range inputs additionally get keyboard support natively
   *  (the focused <input type=range> sees the arrow). */
  onAdjust?: (delta: -1 | 1) => void;
}

export interface MenuNavOptions {
  items: MenuItem[];
  /** Initial focus index. Defaults to 0 (first item in array order). */
  initialIndex?: number;
  /** Whether to fire the hover sound + onFocus hook on the initial mount.
   *  Defaults to false — arriving at a screen already has its own
   *  transition sound (splash whoosh, sweep, modal fade), so a hover sting
   *  on top of that feels gratuitous. The initial focus class is painted
   *  either way so the user sees the starting selection. */
  playInitialHover?: boolean;
  /** When true, arrow-key + Enter handlers call preventDefault + stop
   *  propagation. Use this for menus stacked over another input surface
   *  (e.g. a full-screen modal over a live game) so arrow keys don't
   *  leak into the game's spectate-cycle handler. Defaults to true. */
  captureKeys?: boolean;
  /** Optional B-button (Xbox B / PlayStation ◯) handler — fires once per
   *  press edge while this menu is mounted. Used for "back" / "leave"
   *  affordances: dismiss settings, exit changing room, leave party,
   *  go back from map select. Esc on the keyboard is a separate concern
   *  handled by each screen's own listener. */
  onCancel?: () => void;
  /** When true, holding an arrow key (KBM) or stick / dpad direction
   *  (controller) past a short delay repeats the move at a steady cadence.
   *  Opt-in because most menus are short enough that single-press cadence
   *  is the right UX; long lists (the language picker) benefit from it. */
  holdToRepeat?: boolean;
  /** Scroll behavior used when bringing the focused item into view.
   *  'smooth' is nicer for short lists; 'auto' (instant) is what hold-to-
   *  repeat scrolling needs so rapid focus moves don't queue overlapping
   *  smooth-scroll animations. Defaults to 'smooth'. */
  scrollBehavior?: ScrollBehavior;
}

// Hold-to-repeat timings (keyboard side — gamepad side has its own copies
// in MenuGamepad). Tuned to match desktop OS key-repeat: 400ms before the
// first repeat, ~14 Hz after that.
const HOLD_INITIAL_DELAY_MS = 400;
const HOLD_REPEAT_MS = 70;

const CLASS = 'focused';

export class MenuNav {
  private items: MenuItem[];
  private index = 0;
  private mounted = false;
  private enabled = true;
  private captureKeys: boolean;
  private holdToRepeat: boolean;
  private scrollBehavior: ScrollBehavior;
  private gamepad: MenuGamepad;
  // Per-item listener bundles so we can detach cleanly on dispose.
  private listeners: Array<{ el: HTMLElement; enter: () => void; click: () => void }> = [];
  // Keyboard hold-to-repeat state. Only consulted when holdToRepeat=true.
  // `heldDir` mirrors the currently-pressed arrow direction; the timers
  // tick exactly while it's set.
  private heldDir: MenuGamepadDirection | null = null;
  private holdInitialTimer = 0;
  private holdRepeatTimer = 0;

  constructor(private readonly opts: MenuNavOptions) {
    this.items = opts.items;
    this.index = Math.max(0, Math.min(this.items.length - 1, opts.initialIndex ?? 0));
    this.captureKeys = opts.captureKeys ?? true;
    this.holdToRepeat = opts.holdToRepeat ?? false;
    this.scrollBehavior = opts.scrollBehavior ?? 'smooth';
    this.gamepad = new MenuGamepad({
      onDirection: (d) => this.handleDirection(d),
      onActivate: () => this.activate(),
      onCancel: opts.onCancel ? () => {
        if (!this.enabled) return;
        opts.onCancel!();
      } : undefined
    }, { holdToRepeat: this.holdToRepeat });
  }

  mount(): void {
    if (this.mounted) return;
    this.mounted = true;

    // Per-item mouseenter / click — mirrors the mouse's native hover onto
    // the focus system so a mouse user moving around the menu experiences
    // the same sounds + highlights as a keyboard/controller user.
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i]!;
      const enter = (): void => this.focusIndex(i, /* fromInput */ true);
      const click = (): void => {
        this.focusIndex(i, /* fromInput */ false);
        this.activate('mouse');
      };
      item.el.addEventListener('mouseenter', enter);
      item.el.addEventListener('click', click);
      this.listeners.push({ el: item.el, enter, click });
    }

    addEventListener('keydown', this.onKeyDown, { capture: this.captureKeys });
    if (this.holdToRepeat) {
      addEventListener('keyup', this.onKeyUp, { capture: this.captureKeys });
    }
    this.gamepad.start();

    // Apply initial focus. `playInitialHover` defaults to false semantics
    // for the sound but we DO paint the .focused class so the user sees
    // the starting state immediately.
    this.applyFocusClass();
    if (this.opts.playInitialHover) {
      menuAudio.playHover();
      this.items[this.index]?.onFocus?.();
    }
  }

  dispose(): void {
    if (!this.mounted) return;
    this.mounted = false;
    for (const { el, enter, click } of this.listeners) {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('click', click);
    }
    this.listeners = [];
    removeEventListener('keydown', this.onKeyDown, { capture: this.captureKeys } as EventListenerOptions);
    if (this.holdToRepeat) {
      removeEventListener('keyup', this.onKeyUp, { capture: this.captureKeys } as EventListenerOptions);
    }
    this.endKeyboardHold();
    this.gamepad.stop();
    // Clear focus class so leftover styling doesn't linger if the element
    // is reused elsewhere (modals may be re-rendered into the same slot).
    for (const item of this.items) item.el.classList.remove(CLASS);
  }

  /** Programmatically focus a specific item by index. Used by callers that
   *  want to preset focus after constructing the nav (rare — usually the
   *  initialIndex constructor option is enough). */
  focusIndex(i: number, fromInput: boolean): void {
    if (i < 0 || i >= this.items.length || i === this.index) return;
    this.index = i;
    this.applyFocusClass();
    if (fromInput) {
      menuAudio.playHover();
      this.items[this.index]?.onFocus?.();
    }
  }

  /** Activate the currently focused item. `source` distinguishes mouse
   *  clicks (native DOM bubble side-effects may already have fired) from
   *  keyboard/controller activations (they haven't). Defaults to 'key'
   *  because the gamepad + keydown paths don't pass it explicitly. */
  activate(source: MenuActivationSource = 'key'): void {
    if (!this.enabled) return;
    const item = this.items[this.index];
    if (!item) return;
    menuAudio.playClick();
    item.onActivate(source);
  }

  /** Pause / resume input handling without unmounting. While disabled the
   *  nav swallows keyboard, gamepad, and mouse-click activations; mouse
   *  hover still updates the focus marker visually. The current focus
   *  index is preserved so re-enabling lands the user back where they
   *  were. Used by overlaying modals (ConfirmationPopup, SearchingModal)
   *  to block the underlying screen's nav while they're up. */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      this.gamepad.start();
    } else {
      this.gamepad.stop();
    }
  }

  private applyFocusClass(): void {
    for (let i = 0; i < this.items.length; i++) {
      this.items[i]!.el.classList.toggle(CLASS, i === this.index);
    }
    // Bring the newly-focused item into view if it's inside a scrollable
    // ancestor (e.g. the settings modal's body when the graphics tab
    // overflows). `block: 'nearest'` only scrolls when actually needed,
    // so this is a no-op for menus whose items are already on-screen.
    const focused = this.items[this.index]?.el;
    if (focused) {
      try {
        focused.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.scrollBehavior });
      } catch {
        // Older Safari rejects the options object — fall back to the
        // non-smooth boolean form so the scroll still happens.
        focused.scrollIntoView(false);
      }
    }
  }

  private handleDirection(dir: MenuGamepadDirection): void {
    if (!this.enabled) return;
    const current = this.items[this.index];
    if (!current) return;

    if (dir === 'left' || dir === 'right') {
      // Items with onAdjust own left/right — the focus stays on the slider
      // while the controller scrubs the value, instead of jumping focus to
      // a sibling row that isn't there.
      if (current.onAdjust) {
        current.onAdjust(dir === 'right' ? 1 : -1);
        return;
      }
      const sameRow = this.items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => it.row === current.row);
      const ordered = sameRow.sort((a, b) => a.it.col - b.it.col);
      const pos = ordered.findIndex(({ i }) => i === this.index);
      if (pos < 0) return;
      const next = dir === 'left' ? pos - 1 : pos + 1;
      if (next < 0 || next >= ordered.length) return;
      this.focusIndex(ordered[next]!.i, true);
      return;
    }

    // Up/Down: find the nearest item in the adjacent row whose col is
    // closest to the current col. "Adjacent" means the next row in the
    // requested direction that has any items — so empty intermediate rows
    // are skipped, which matters if a screen's rows have gaps.
    const targetRow = this.findAdjacentRow(current.row, dir === 'up' ? -1 : +1);
    if (targetRow == null) return;
    const inRow = this.items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.row === targetRow);
    if (inRow.length === 0) return;
    let best = inRow[0]!;
    let bestD = Math.abs(best.it.col - current.col);
    for (let k = 1; k < inRow.length; k++) {
      const d = Math.abs(inRow[k]!.it.col - current.col);
      // Ties go to the higher column — arbitrary but consistent, keeps
      // repeated presses predictable.
      if (d < bestD || (d === bestD && inRow[k]!.it.col > best.it.col)) {
        best = inRow[k]!;
        bestD = d;
      }
    }
    this.focusIndex(best.i, true);
  }

  private findAdjacentRow(fromRow: number, delta: -1 | 1): number | null {
    const rows = Array.from(new Set(this.items.map((it) => it.row))).sort((a, b) => a - b);
    const pos = rows.indexOf(fromRow);
    if (pos < 0) return null;
    const nextPos = pos + delta;
    if (nextPos < 0 || nextPos >= rows.length) return null;
    return rows[nextPos]!;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.mounted) return;
    if (!this.enabled) return;
    // Let the user type inside text inputs — unless the key is one of the
    // navigation keys, in which case we handle it globally (so pressing Up
    // from the name input jumps to the dice button / hat grid).
    let dir: MenuGamepadDirection | null = null;
    if (e.code === 'ArrowUp') dir = 'up';
    else if (e.code === 'ArrowDown') dir = 'down';
    else if (e.code === 'ArrowLeft') dir = 'left';
    else if (e.code === 'ArrowRight') dir = 'right';

    if (dir) {
      // Range sliders ("type=range") natively bind ArrowLeft / ArrowRight to
      // value adjustment — that's exactly the UX we want for volume / mouse
      // sensitivity sliders. Bail out without preventDefault so the native
      // handling fires.
      const ae = document.activeElement;
      if (ae instanceof HTMLInputElement && ae.type === 'range'
          && (dir === 'left' || dir === 'right')) {
        return;
      }
      if (this.captureKeys) { e.preventDefault(); e.stopPropagation(); }
      // When holdToRepeat is on, we manage our own cadence — swallow the
      // OS auto-repeat events and fire one move on the initial press,
      // then schedule our own repeats on a timer.
      if (this.holdToRepeat) {
        if (e.repeat) return;
        this.beginKeyboardHold(dir);
      }
      this.handleDirection(dir);
      return;
    }

    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      // Don't swallow Enter inside contenteditable or other non-input edit
      // contexts (defensive — the current menus only have plain <input>).
      if (this.captureKeys) { e.preventDefault(); e.stopPropagation(); }
      this.activate();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.mounted) return;
    let dir: MenuGamepadDirection | null = null;
    if (e.code === 'ArrowUp') dir = 'up';
    else if (e.code === 'ArrowDown') dir = 'down';
    else if (e.code === 'ArrowLeft') dir = 'left';
    else if (e.code === 'ArrowRight') dir = 'right';
    if (dir && this.heldDir === dir) this.endKeyboardHold();
  };

  private beginKeyboardHold(dir: MenuGamepadDirection): void {
    this.endKeyboardHold();
    this.heldDir = dir;
    this.holdInitialTimer = setTimeout(() => {
      this.holdInitialTimer = 0;
      this.holdRepeatTimer = setInterval(() => {
        // Direction may have been cleared (keyup, dispose) between fires.
        if (!this.heldDir || !this.enabled) return;
        this.handleDirection(this.heldDir);
      }, HOLD_REPEAT_MS) as unknown as number;
    }, HOLD_INITIAL_DELAY_MS) as unknown as number;
  }

  private endKeyboardHold(): void {
    this.heldDir = null;
    if (this.holdInitialTimer) { clearTimeout(this.holdInitialTimer); this.holdInitialTimer = 0; }
    if (this.holdRepeatTimer) { clearInterval(this.holdRepeatTimer); this.holdRepeatTimer = 0; }
  }
}
